import { select } from "d3";

import {
  AnimatedGrowthLabel,
  AppletElement,
  CATERPILLAR_DIAGRAM_HEIGHT,
  CATERPILLAR_DIAGRAM_WIDTH,
  CATERPILLAR_EDGE_STROKE,
  CATERPILLAR_LEGS_BY_SPINE,
  CATERPILLAR_NODE_STROKE,
  CATERPILLAR_SPINE_IDS,
  CATERPILLAR_STRUCTURE_NODES,
  CaterpillarDiagramEdge,
  CaterpillarDiagramNode,
  CaterpillarNodeKind,
  EdgeEndpoints,
  GROWTH_BACKGROUND_EDGE_STROKE,
  GROWTH_BACKGROUND_NODE_FILL,
  GROWTH_BACKGROUND_NODE_STROKE,
  GROWTH_BACKGROUND_OPACITY,
  GroupSelection,
  appendStepControls,
  caterpillarNodeFill,
  caterpillarNodeLookup,
  caterpillarNodeStrokeWidth,
  renderAnimatedGrowthLabels,
  renderApplet,
  renderCaterpillarLegend,
  trimmedEdgeEndpoints,
} from "./common";

type CaterpillarGrowthParity = "zero" | "max";

type CaterpillarGrowthStep = Readonly<{
  source: string;
  target: string;
  parity: CaterpillarGrowthParity;
}>;

type CaterpillarLabelMap = Readonly<Record<string, number>>;

type CaterpillarGrowthState = Readonly<{
  step: number;
  labels: CaterpillarLabelMap;
  visibleNodeIds: readonly string[];
  visibleEdges: readonly CaterpillarDiagramEdge[];
  maxLabel: number;
}>;

const CATERPILLAR_ACTIVE_FILL = "#dceaa8";
const CATERPILLAR_GROWTH_SPINE_NODE_RADIUS = 12;
const CATERPILLAR_GROWTH_LEG_NODE_RADIUS = 9;
const CATERPILLAR_GROWTH_LABEL_FILL = "#263726";

const CATERPILLAR_GROWTH_STATES = buildCaterpillarGrowthStates();
const CATERPILLAR_GROWTH_FINAL_STATE = CATERPILLAR_GROWTH_STATES[CATERPILLAR_GROWTH_STATES.length - 1];

function caterpillarGrowthSteps(): readonly CaterpillarGrowthStep[] {
  const steps: CaterpillarGrowthStep[] = [];

  CATERPILLAR_SPINE_IDS.forEach((spineId: string, spineIndex: number): void => {
    const parity: CaterpillarGrowthParity = spineIndex % 2 === 0 ? "zero" : "max";
    const legs = CATERPILLAR_LEGS_BY_SPINE[spineId] ?? [];

    legs.forEach((legId: string): void => {
      steps.push({ source: spineId, target: legId, parity });
    });

    const nextSpineId = CATERPILLAR_SPINE_IDS[spineIndex + 1];
    if (nextSpineId !== undefined) {
      steps.push({ source: spineId, target: nextSpineId, parity });
    }
  });

  return steps;
}

function buildCaterpillarGrowthStates(): readonly CaterpillarGrowthState[] {
  let labels: Record<string, number> = { s0: 0 };
  let visibleNodeIds: readonly string[] = ["s0"];
  let visibleEdges: readonly CaterpillarDiagramEdge[] = [];
  const states: CaterpillarGrowthState[] = [{
    step: 0,
    labels: { ...labels },
    visibleNodeIds: [...visibleNodeIds],
    visibleEdges: [...visibleEdges],
    maxLabel: 0,
  }];

  caterpillarGrowthSteps().forEach((growthStep: CaterpillarGrowthStep): void => {
    const q = visibleEdges.length;
    const nextLabels: Record<string, number> = {};

    if (growthStep.parity === "zero") {
      Object.entries(labels).forEach(([nodeId, label]: [string, number]): void => {
        nextLabels[nodeId] = label;
      });
      nextLabels[growthStep.target] = q + 1;
    } else {
      Object.entries(labels).forEach(([nodeId, label]: [string, number]): void => {
        nextLabels[nodeId] = label + 1;
      });
      nextLabels[growthStep.target] = 0;
    }

    labels = nextLabels;
    visibleNodeIds = [...visibleNodeIds, growthStep.target];
    visibleEdges = [...visibleEdges, { source: growthStep.source, target: growthStep.target }];
    states.push({
      step: visibleEdges.length,
      labels: { ...labels },
      visibleNodeIds: [...visibleNodeIds],
      visibleEdges: [...visibleEdges],
      maxLabel: visibleEdges.length,
    });
  });

  return states;
}

function caterpillarGrowthNodeRadius(kind: CaterpillarNodeKind): number {
  return kind === "leg" ? CATERPILLAR_GROWTH_LEG_NODE_RADIUS : CATERPILLAR_GROWTH_SPINE_NODE_RADIUS;
}

function caterpillarGrowthNodes(state: CaterpillarGrowthState): readonly CaterpillarDiagramNode[] {
  const nodes = caterpillarNodeLookup();
  return state.visibleNodeIds.reduce<CaterpillarDiagramNode[]>((visibleNodes, nodeId: string): CaterpillarDiagramNode[] => {
    const node = nodes.get(nodeId);
    if (node !== undefined) {
      visibleNodes.push(node);
    }
    return visibleNodes;
  }, []);
}

function caterpillarGrowthEdgeEndpoints(
  edge: CaterpillarDiagramEdge,
  nodes: ReadonlyMap<string, CaterpillarDiagramNode>,
): EdgeEndpoints {
  const source = nodes.get(edge.source);
  const target = nodes.get(edge.target);
  if (source === undefined || target === undefined) {
    return { x1: 0, y1: 0, x2: 0, y2: 0 };
  }

  return trimmedEdgeEndpoints(
    source,
    target,
    caterpillarGrowthNodeRadius(source.kind),
    caterpillarGrowthNodeRadius(target.kind),
  );
}

function caterpillarGrowthNodeFill(state: CaterpillarGrowthState, node: CaterpillarDiagramNode): string {
  const label = state.labels[node.id];
  if (label === 0 || label === state.maxLabel) {
    return CATERPILLAR_ACTIVE_FILL;
  }

  return caterpillarNodeFill(node.kind);
}

function caterpillarGrowthLabels(state: CaterpillarGrowthState): readonly AnimatedGrowthLabel[] {
  return caterpillarGrowthNodes(state).map((node: CaterpillarDiagramNode): AnimatedGrowthLabel => ({
    id: node.id,
    label: state.labels[node.id].toString(),
    x: node.x,
    y: node.y + 0.5,
    fontSize: state.labels[node.id] >= 10 ? 9 : 11,
    fill: CATERPILLAR_GROWTH_LABEL_FILL,
  }));
}

function renderCaterpillarGrowthBackground(graph: GroupSelection): void {
  const nodes = caterpillarNodeLookup();
  const background = graph.append<SVGGElement>("g")
    .attr("class", "caterpillar-growth-background")
    .attr("opacity", GROWTH_BACKGROUND_OPACITY)
    .attr("pointer-events", "none");

  background.append<SVGGElement>("g")
    .attr("class", "caterpillar-growth-background-edges")
    .selectAll<SVGLineElement, CaterpillarDiagramEdge>("line")
    .data(CATERPILLAR_GROWTH_FINAL_STATE.visibleEdges)
    .join("line")
    .attr("x1", (edge: CaterpillarDiagramEdge): number => caterpillarGrowthEdgeEndpoints(edge, nodes).x1)
    .attr("y1", (edge: CaterpillarDiagramEdge): number => caterpillarGrowthEdgeEndpoints(edge, nodes).y1)
    .attr("x2", (edge: CaterpillarDiagramEdge): number => caterpillarGrowthEdgeEndpoints(edge, nodes).x2)
    .attr("y2", (edge: CaterpillarDiagramEdge): number => caterpillarGrowthEdgeEndpoints(edge, nodes).y2)
    .attr("stroke", GROWTH_BACKGROUND_EDGE_STROKE)
    .attr("stroke-width", 1.1)
    .attr("stroke-linecap", "round");

  background.append<SVGGElement>("g")
    .attr("class", "caterpillar-growth-background-nodes")
    .selectAll<SVGCircleElement, CaterpillarDiagramNode>("circle")
    .data(CATERPILLAR_STRUCTURE_NODES)
    .join("circle")
    .attr("cx", (node: CaterpillarDiagramNode): number => node.x)
    .attr("cy", (node: CaterpillarDiagramNode): number => node.y)
    .attr("r", (node: CaterpillarDiagramNode): number => caterpillarGrowthNodeRadius(node.kind))
    .attr("fill", GROWTH_BACKGROUND_NODE_FILL)
    .attr("stroke", GROWTH_BACKGROUND_NODE_STROKE)
    .attr("stroke-width", 1.2);
}

function renderCaterpillarGrowthFrame(
  graph: GroupSelection,
  state: CaterpillarGrowthState,
  element: AppletElement,
  previousState?: CaterpillarGrowthState,
): void {
  element.dataset.growthStep = state.step.toString();
  element.dataset.growthMax = state.maxLabel.toString();
  graph.selectAll("*").remove();

  const nodes = caterpillarNodeLookup();
  const visibleNodes = caterpillarGrowthNodes(state);

  renderCaterpillarGrowthBackground(graph);

  graph.append<SVGGElement>("g")
    .attr("class", "caterpillar-growth-edges")
    .selectAll<SVGLineElement, CaterpillarDiagramEdge>("line")
    .data(state.visibleEdges)
    .join("line")
    .attr("x1", (edge: CaterpillarDiagramEdge): number => caterpillarGrowthEdgeEndpoints(edge, nodes).x1)
    .attr("y1", (edge: CaterpillarDiagramEdge): number => caterpillarGrowthEdgeEndpoints(edge, nodes).y1)
    .attr("x2", (edge: CaterpillarDiagramEdge): number => caterpillarGrowthEdgeEndpoints(edge, nodes).x2)
    .attr("y2", (edge: CaterpillarDiagramEdge): number => caterpillarGrowthEdgeEndpoints(edge, nodes).y2)
    .attr("stroke", CATERPILLAR_EDGE_STROKE)
    .attr("stroke-width", 1.4);

  graph.append<SVGGElement>("g")
    .attr("class", "caterpillar-growth-nodes")
    .selectAll<SVGCircleElement, CaterpillarDiagramNode>("circle")
    .data(visibleNodes)
    .join("circle")
    .attr("cx", (node: CaterpillarDiagramNode): number => node.x)
    .attr("cy", (node: CaterpillarDiagramNode): number => node.y)
    .attr("r", (node: CaterpillarDiagramNode): number => caterpillarGrowthNodeRadius(node.kind))
    .attr("fill", (node: CaterpillarDiagramNode): string => caterpillarGrowthNodeFill(state, node))
    .attr("stroke", CATERPILLAR_NODE_STROKE)
    .attr("stroke-width", (node: CaterpillarDiagramNode): number => caterpillarNodeStrokeWidth(node.kind))
    .attr("data-node-kind", (node: CaterpillarDiagramNode): CaterpillarNodeKind => node.kind);

  renderAnimatedGrowthLabels(
    graph,
    "caterpillar-growth-labels",
    caterpillarGrowthLabels(state),
    previousState === undefined ? undefined : caterpillarGrowthLabels(previousState),
  );
}

function renderCaterpillarGrowth(element: AppletElement): void {
  element.dataset.rendered = "true";
  element.replaceChildren();

  const svg = select<AppletElement, unknown>(element)
    .append<SVGSVGElement>("svg")
    .attr("viewBox", `0 0 ${CATERPILLAR_DIAGRAM_WIDTH} ${CATERPILLAR_DIAGRAM_HEIGHT}`)
    .attr("width", CATERPILLAR_DIAGRAM_WIDTH)
    .attr("height", CATERPILLAR_DIAGRAM_HEIGHT)
    .attr("role", "img")
    .attr("aria-label", "Animated construction of a gracefully labeled caterpillar by extreme sprout moves.");

  const graph = svg.append<SVGGElement>("g")
    .attr("class", "caterpillar-growth-graph");
  let previousState: CaterpillarGrowthState | undefined;
  let previousFrameIndex: number | undefined;

  renderCaterpillarLegend(svg);

  appendStepControls({
    element,
    frameCount: CATERPILLAR_GROWTH_STATES.length,
    onFrameChange: (frameIndex: number): void => {
      const state = CATERPILLAR_GROWTH_STATES[frameIndex];
      const shouldAnimateLabels = previousFrameIndex !== undefined && frameIndex > previousFrameIndex;
      renderCaterpillarGrowthFrame(graph, state, element, shouldAnimateLabels ? previousState : undefined);
      previousState = state;
      previousFrameIndex = frameIndex;
    },
  });
}

renderApplet(".caterpillar-growth-applet", renderCaterpillarGrowth);
